import { baseApiSlice } from '../../commons/base-api.config';
import {
    API_BRAND_MAGIC_FETCH_COPY_INFO,
    API_BRAND_MAGIC_VOICE,
    API_BRAND_MAGIC_ADD_COLOR,
    API_BRAND_MAGIC_GET_BRAND_ELEMENTS,
    API_BRAND_MAGIC_REMOVE_COLOR,
    API_BRAND_MAGIC_ADD_FONT,
    API_BRAND_MAGIC_REMOVE_FONT,
    API_DEFAULT_POTIONS,
    API_BRAND_IMAGE_GET_BRAND_FAVOURITES,
    API_BRAND_MAGIC_SCRAPING_URL,
    API_BRAND_MAGIC_ADD_LOGO,
    API_BRAND_MAGIC_REMOVE_LOGO,
    API_BRAND_MAGIC_ADD_COLORS,
} from '../../constants/api';
import { HTTP_METHODS } from '../../constants/http';
import {
    IAddBrandElementLogo,
    IAddBrandElementsFont,
    IAddColor,
    IAddColors,
    IBrandVoiceState,
    IGetBrandElements,
    IOnBoarding,
    IRemoveBrandElementsFont,
    IRemoveColor,
} from '../../interfaces/brand-magic.interface';

export const brandMagicApiSlice = baseApiSlice.injectEndpoints({
    endpoints(builder) {
        return {
            getDefaultPotions: builder.query<any, void>({
                query() {
                    return {
                        url: API_DEFAULT_POTIONS,
                        method: HTTP_METHODS.GET,
                    };
                },
                providesTags: [],
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            getBrandImageFavourites: builder.query<any, number>({
                query(brandId) {
                    return {
                        url: `${API_BRAND_IMAGE_GET_BRAND_FAVOURITES}/?brandId=${brandId}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                providesTags: ['imagePotion'],
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            deleteImageFavouritesById: builder.mutation({
                query: (id) => {
                    return {
                        url: `${API_BRAND_IMAGE_GET_BRAND_FAVOURITES}/${id}`,
                        method: HTTP_METHODS.DELETE,
                    };
                },
                invalidatesTags: ['imagePotion'],
                transformResponse: (data: { result: any }) => data?.result,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            saveBrandVoice: builder.mutation<any, IBrandVoiceState>({
                query: (data) => {
                    return {
                        url: API_BRAND_MAGIC_VOICE,
                        method: HTTP_METHODS.PATCH,
                        body: data,
                    };
                },
                invalidatesTags: ['brandVoice'],
                transformResponse: (data: { result: any }) => data?.result,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            getBrandVoice: builder.query<any, number>({
                query(brandId) {
                    return {
                        url: `${API_BRAND_MAGIC_VOICE}/?brandId=${brandId}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                providesTags: ['brandVoice'],
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            getBrandMagicCopyInfo: builder.query<any, number>({
                query(brandId) {
                    return {
                        url: `${API_BRAND_MAGIC_FETCH_COPY_INFO}/?brandId=${brandId}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            // design elements
            getBrandElements: builder.query<IGetBrandElements, number>({
                query(brandId) {
                    return {
                        url: `${API_BRAND_MAGIC_GET_BRAND_ELEMENTS}/?brandId=${brandId}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            addBrandElementsColor: builder.mutation<IGetBrandElements, IAddColor>({
                query: (data) => {
                    return {
                        url: API_BRAND_MAGIC_ADD_COLOR,
                        method: HTTP_METHODS.PATCH,
                        body: data,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            addBrandElementsColors: builder.mutation<IGetBrandElements, IAddColors>({
                query: (data) => {
                    return {
                        url: API_BRAND_MAGIC_ADD_COLORS,
                        method: HTTP_METHODS.PATCH,
                        body: data,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            deleteBrandElementsColor: builder.mutation<IGetBrandElements, IRemoveColor>({
                query: (data) => {
                    return {
                        url: API_BRAND_MAGIC_REMOVE_COLOR,
                        method: HTTP_METHODS.PATCH,
                        body: data,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            addBrandElementsFont: builder.mutation<IGetBrandElements, IAddBrandElementsFont>({
                query: (data) => {
                    const formData = new FormData();
                    formData.append('fontFile', data.fontFile as File);
                    formData.append('fontClass', data.fontClass);
                    formData.append('fontFamily', data.fontFamily);
                    formData.append('brandId', data.brandId.toString());

                    return {
                        url: API_BRAND_MAGIC_ADD_FONT,
                        method: HTTP_METHODS.PATCH,
                        body: formData,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            removeBrandElementsFont: builder.mutation<IGetBrandElements, IRemoveBrandElementsFont>({
                query: (data) => {
                    return {
                        url: API_BRAND_MAGIC_REMOVE_FONT,
                        method: HTTP_METHODS.PATCH,
                        body: data,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            uploadBrandElementsLogo: builder.mutation<IGetBrandElements, IAddBrandElementLogo>({
                query: (data) => {
                    const formData = new FormData();
                    formData.append('logo', data.logo as File);
                    formData.append('brandId', data.brandId.toString());
                    return {
                        url: API_BRAND_MAGIC_ADD_LOGO,
                        method: HTTP_METHODS.POST,
                        body: formData,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            removeBrandElementsLogo: builder.mutation<IGetBrandElements, number>({
                query: (brandId) => {
                    return {
                        url: `${API_BRAND_MAGIC_REMOVE_LOGO}/?brandId=${brandId}`,
                        method: HTTP_METHODS.DELETE,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            saveScrapingURL: builder.mutation<any, IOnBoarding>({
                query: (data) => {
                    return {
                        url: API_BRAND_MAGIC_SCRAPING_URL,
                        method: HTTP_METHODS.POST,
                        body: data,
                    };
                },
                transformResponse: (data) => data,
                transformErrorResponse: (response) => response,
            }),
        };
    },
});

export const {
    useGetDefaultPotionsQuery,
    useGetBrandImageFavouritesQuery,
    useDeleteImageFavouritesByIdMutation,
    useSaveBrandVoiceMutation,
    useLazyGetBrandVoiceQuery,
    useLazyGetBrandMagicCopyInfoQuery,
    // brand elements
    useLazyGetBrandElementsQuery,
    useAddBrandElementsColorMutation,
    useAddBrandElementsColorsMutation,
    useDeleteBrandElementsColorMutation,
    useAddBrandElementsFontMutation,
    useRemoveBrandElementsFontMutation,
    useUploadBrandElementsLogoMutation,
    useRemoveBrandElementsLogoMutation,
    //Scraping url
    useSaveScrapingURLMutation,
} = brandMagicApiSlice;
